import React, { useState, useEffect, useRef } from 'react'
import { validateEmail } from '../../lib/supabase'
import { validatePasswordStrength } from '../../lib/passwordSecurity'
import { EmailVerificationModal } from '../EmailVerificationModal'
import { logAdminAction } from '../../lib/adminAuth'
import { getAllCountries } from '../../lib/data/completeCountryList'
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  onUserAdded: () => void
  adminUser?: any
}

interface FormData {
  email: string
  password: string
  confirmPassword: string
  fullName: string
  username: string
  phone: string
  countryOfResidence: string
  sponsorUsername: string
  isAdmin: boolean
  sendWelcomeEmail: boolean
}

interface FormErrors {
  [key: string]: string
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onUserAdded, adminUser }) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    username: '',
    phone: '',
    countryOfResidence: '', // FIXED: No default - admin must select country
    sponsorUsername: '',
    isAdmin: false,
    sendWelcomeEmail: true
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [generalError, setGeneralError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [createdUserId, setCreatedUserId] = useState<number | null>(null)

  // Password visibility states (default to visible)
  const [showPassword, setShowPassword] = useState(true)
  const [showConfirmPassword, setShowConfirmPassword] = useState(true)
  const [sponsorValidation, setSponsorValidation] = useState<{
    isValidating: boolean
    isValid: boolean | null
    sponsorName: string | null
  }>({
    isValidating: false,
    isValid: null,
    sponsorName: null
  })

  const [emailValidation, setEmailValidation] = useState<{
    isValidating: boolean
    isValid: boolean | null
    errorMessage: string | null
  }>({
    isValidating: false,
    isValid: null,
    errorMessage: null
  })

  const [usernameValidation, setUsernameValidation] = useState<{
    isValidating: boolean
    isValid: boolean | null
    errorMessage: string | null
  }>({
    isValidating: false,
    isValid: null,
    errorMessage: null
  })

  // Get all countries for dropdown
  const allCountries = getAllCountries()

  // Debounce timers
  const emailDebounceTimer = useRef<NodeJS.Timeout | null>(null)
  const usernameDebounceTimer = useRef<NodeJS.Timeout | null>(null)
  const sponsorDebounceTimer = useRef<NodeJS.Timeout | null>(null)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Clear all debounce timers
      if (emailDebounceTimer.current) {
        clearTimeout(emailDebounceTimer.current)
      }
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current)
      }
      if (sponsorDebounceTimer.current) {
        clearTimeout(sponsorDebounceTimer.current)
      }

      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        username: '',
        phone: '',
        countryOfResidence: '', // FIXED: No default - admin must select country
        sponsorUsername: '',
        isAdmin: false,
        sendWelcomeEmail: true
      })
      setErrors({})
      setGeneralError('')
      setSuccessMessage('')
      setCreatedUserId(null)
      setSponsorValidation({
        isValidating: false,
        isValid: null,
        sponsorName: null
      })
      setEmailValidation({
        isValidating: false,
        isValid: null,
        errorMessage: null
      })
      setUsernameValidation({
        isValidating: false,
        isValid: null,
        errorMessage: null
      })
    }
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }

    // Auto-generate username from full name if username is empty
    if (name === 'fullName' && !formData.username) {
      const generatedUsername = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 20)

      if (generatedUsername) {
        setFormData(prev => ({ ...prev, username: generatedUsername }))
      }
    }

    // Validate email in real-time (CRITICAL)
    if (name === 'email') {
      const email = value.trim().toLowerCase()

      // Clear previous timer
      if (emailDebounceTimer.current) {
        clearTimeout(emailDebounceTimer.current)
      }

      if (email && email.includes('@')) {
        setEmailValidation({ isValidating: true, isValid: null, errorMessage: null })

        // Debounce the validation
        emailDebounceTimer.current = setTimeout(async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ operation: 'check_email_exists', data: { email } })
            })
            const result = await response.json()

            if (!response.ok) {
              console.error('Email validation error:', result.error)
              setEmailValidation({
                isValidating: false,
                isValid: null,
                errorMessage: 'Could not validate email'
              })
              return
            }

            // Check if email exists
            if (result.data?.exists) {
              setEmailValidation({
                isValidating: false,
                isValid: false,
                errorMessage: 'Email already exists'
              })
            } else {
              setEmailValidation({
                isValidating: false,
                isValid: true,
                errorMessage: null
              })
            }
          } catch (error) {
            console.error('Email validation exception:', error)
            setEmailValidation({
              isValidating: false,
              isValid: null,
              errorMessage: 'Could not validate email'
            })
          }
        }, 800) // 800ms debounce
      } else {
        setEmailValidation({
          isValidating: false,
          isValid: null,
          errorMessage: null
        })
      }
    }

    // Validate username in real-time (CRITICAL)
    if (name === 'username') {
      const username = value.trim().toLowerCase()

      // Clear previous timer
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current)
      }

      if (username && username.length >= 2) {
        setUsernameValidation({ isValidating: true, isValid: null, errorMessage: null })

        // Debounce the validation
        usernameDebounceTimer.current = setTimeout(async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ operation: 'check_username_exists', data: { username } })
            })
            const result = await response.json()

            if (!response.ok) {
              console.error('Username validation error:', result.error)
              setUsernameValidation({
                isValidating: false,
                isValid: null,
                errorMessage: 'Could not validate username'
              })
              return
            }

            if (result.data?.exists) {
              setUsernameValidation({
                isValidating: false,
                isValid: false,
                errorMessage: 'Username already exists'
              })
            } else {
              setUsernameValidation({
                isValidating: false,
                isValid: true,
                errorMessage: null
              })
            }
          } catch (error) {
            console.error('Username validation exception:', error)
            setUsernameValidation({
              isValidating: false,
              isValid: null,
              errorMessage: 'Could not validate username'
            })
          }
        }, 800) // 800ms debounce
      } else {
        setUsernameValidation({
          isValidating: false,
          isValid: null,
          errorMessage: null
        })
      }
    }

    // Validate sponsor username in real-time
    if (name === 'sponsorUsername') {
      const sponsorUsername = value.trim()

      // Clear previous timer
      if (sponsorDebounceTimer.current) {
        clearTimeout(sponsorDebounceTimer.current)
      }

      if (sponsorUsername) {
        setSponsorValidation({ isValidating: true, isValid: null, sponsorName: null })

        // Debounce the validation
        sponsorDebounceTimer.current = setTimeout(async () => {
          console.log('🔍 Validating sponsor username:', sponsorUsername)
          try {
            const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ operation: 'find_sponsor', data: { sponsorUsername } })
            })
            const result = await response.json()

            console.log('📊 Sponsor API result:', result)

            if (!response.ok) {
              console.error('❌ Sponsor validation error:', result.error)
              setSponsorValidation({
                isValidating: false,
                isValid: false,
                sponsorName: null
              })
              return
            }

            if (!result.data?.found) {
              console.log('❌ No sponsor found')
              setSponsorValidation({
                isValidating: false,
                isValid: false,
                sponsorName: null
              })
            } else {
              console.log('✅ Sponsor found:', result.data)
              setSponsorValidation({
                isValidating: false,
                isValid: true,
                sponsorName: result.data.sponsorName
              })
            }
          } catch (error) {
            console.error('💥 Sponsor validation exception:', error)
            setSponsorValidation({
              isValidating: false,
              isValid: false,
              sponsorName: null
            })
          }
        }, 800) // 800ms debounce
      } else {
        setSponsorValidation({
          isValidating: false,
          isValid: null,
          sponsorName: null
        })
      }
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Email validation (CRITICAL - check real-time validation)
    const emailCheck = validateEmail(formData.email)
    if (!emailCheck.valid) {
      newErrors.email = emailCheck.error || 'Invalid email'
    } else if (emailValidation.isValidating) {
      newErrors.email = 'Please wait while email is being validated'
    } else if (emailValidation.isValid === false) {
      newErrors.email = emailValidation.errorMessage || 'Email already exists'
    } else if (emailValidation.isValid === null && formData.email.trim()) {
      newErrors.email = 'Email validation incomplete - please wait'
    }

    // Username validation (CRITICAL - check real-time validation)
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 2) {
      newErrors.username = 'Username must be at least 2 characters'
    } else if (formData.username.length > 25) {
      newErrors.username = 'Username must be 25 characters or less'
    } else if (usernameValidation.isValidating) {
      newErrors.username = 'Please wait while username is being validated'
    } else if (usernameValidation.isValid === false) {
      newErrors.username = usernameValidation.errorMessage || 'Username already exists'
    } else if (usernameValidation.isValid === null && formData.username.trim()) {
      newErrors.username = 'Username validation incomplete - please wait'
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordValidation = validatePasswordStrength(formData.password, formData.username)
      if (!passwordValidation.valid) {
        newErrors.password = passwordValidation.errors[0] || 'Password is too weak'
      }
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Full name
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }

    // Phone
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    // Country
    if (!formData.countryOfResidence) {
      newErrors.countryOfResidence = 'Country is required'
    }

    // Sponsor username validation (CRITICAL)
    if (formData.sponsorUsername.trim()) {
      if (sponsorValidation.isValidating) {
        newErrors.sponsorUsername = 'Please wait while sponsor is being validated'
      } else if (sponsorValidation.isValid === false) {
        newErrors.sponsorUsername = 'Sponsor username not found in database'
      } else if (sponsorValidation.isValid === null) {
        newErrors.sponsorUsername = 'Sponsor validation incomplete'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }



  const createUser = async (): Promise<number | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'create_user',
          data: {
            email: formData.email,
            password: formData.password,
            fullName: formData.fullName,
            username: formData.username,
            phone: formData.phone,
            countryOfResidence: formData.countryOfResidence,
            sponsorUsername: formData.sponsorUsername,
            isAdmin: formData.isAdmin
          }
        })
      })
      const result = await response.json()

      if (!response.ok) {
        console.error('Error creating user:', result.error)
        setGeneralError(`Failed to create user: ${result.error}`)
        return null
      }

      console.log('✅ User created successfully:', result.data)

      // Check for sponsor warning
      if (result.data.sponsorWarning) {
        console.warn('⚠️ Sponsor warning:', result.data.sponsorWarning)
        alert(`⚠️ WARNING: ${result.data.sponsorWarning}\n\nYou may need to manually add the sponsor relationship.`)
      }

      // Log admin action
      try {
        await logAdminAction(
          adminUser?.email || 'unknown',
          'CREATE_USER',
          'user',
          result.data.userId.toString(),
          {
            username: formData.username,
            email: formData.email,
            fullName: formData.fullName,
            isAdmin: formData.isAdmin,
            sponsorUsername: formData.sponsorUsername || null,
            emailVerificationRequested: formData.sendWelcomeEmail
          }
        )
      } catch (auditError) {
        console.warn('Failed to log admin action (user still created):', auditError)
      }

      return result.data.userId
    } catch (error) {
      console.error('Error creating user:', error)
      setGeneralError('Failed to create user. Please try again.')
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setGeneralError('')
    setSuccessMessage('')

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Real-time validation already checked for duplicates
      // Create the user
      const userId = await createUser()
      if (!userId) {
        setLoading(false)
        return
      }

      setCreatedUserId(userId)

      // Commission balance is now initialized by the API's create_user operation

      // Send welcome email with credentials if requested
      if (formData.sendWelcomeEmail) {
        try {
          console.log('📧 [AddUserModal] Sending welcome email with credentials...')

          const loginUrl = `${window.location.origin}/login`

          const emailResponse = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({
              emailType: 'welcome',
              data: {
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName,
                password: formData.password, // Send the plain password
                loginUrl: loginUrl
              }
            })
          })

          const emailResult = await emailResponse.json()

          if (emailResult.success) {
            console.log('✅ [AddUserModal] Welcome email sent successfully')
          } else {
            console.warn('⚠️ [AddUserModal] Welcome email failed:', emailResult.error)
            // Don't fail the whole operation if email fails
          }
        } catch (emailError) {
          console.error('❌ [AddUserModal] Error sending welcome email:', emailError)
          // Don't fail the whole operation if email fails
        }

        // Mark as verified and complete
        await markUserAsVerified(userId)

        // Show success message
        setSuccessMessage(`✅ User "${formData.username}" created successfully! Welcome email sent to ${formData.email}`)

        // Wait 2 seconds to show success message, then close
        setTimeout(() => {
          onUserAdded()
          onClose()
        }, 2000)
      } else {
        // Mark as complete without email
        await markUserAsVerified(userId)

        // Show success message
        setSuccessMessage(`✅ User "${formData.username}" created successfully!`)

        // Wait 2 seconds to show success message, then close
        setTimeout(() => {
          onUserAdded()
          onClose()
        }, 2000)
      }
    } catch (error) {
      console.error('Error in form submission:', error)
      setGeneralError('Failed to create user. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const markUserAsVerified = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'mark_email_verified', data: { userId } })
      })
      if (!response.ok) {
        const result = await response.json()
        console.error('Error marking user as verified:', result.error)
      }
    } catch (error) {
      console.error('Error marking user as verified:', error)
    }
  }

  const handleEmailVerificationSuccess = async () => {
    if (createdUserId) {
      await markUserAsVerified(createdUserId)
      setShowEmailVerification(false)
      onUserAdded()
      onClose()
    }
  }

  const handleEmailVerificationSkip = () => {
    setShowEmailVerification(false)
    onUserAdded()
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">➕ Add New User</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {generalError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {generalError}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors.email || emailValidation.isValid === false ? 'border-red-500' :
                      emailValidation.isValid === true ? 'border-green-500' : 'border-gray-600'
                    }`}
                    placeholder="user@example.com"
                    required
                  />
                  {emailValidation.isValidating && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                    </div>
                  )}
                  {emailValidation.isValid === true && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-400">
                      ✓
                    </div>
                  )}
                  {emailValidation.isValid === false && formData.email.trim() && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400">
                      ✗
                    </div>
                  )}
                </div>
                {emailValidation.isValid === true && (
                  <p className="text-green-400 text-sm mt-1">
                    ✓ Email is available
                  </p>
                )}
                {emailValidation.isValid === false && formData.email.trim() && (
                  <p className="text-red-400 text-sm mt-1">
                    ✗ {emailValidation.errorMessage || 'Email already exists'}
                  </p>
                )}
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                        errors.password ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Strong password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                        errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder="Confirm password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              {/* Full Name and Username */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors.fullName ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="John Doe"
                    required
                  />
                  {errors.fullName && <p className="text-red-400 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Username *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-10 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                        errors.username || usernameValidation.isValid === false ? 'border-red-500' :
                        usernameValidation.isValid === true ? 'border-green-500' : 'border-gray-600'
                      }`}
                      placeholder="johndoe"
                      required
                    />
                    {usernameValidation.isValidating && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                      </div>
                    )}
                    {usernameValidation.isValid === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-400">
                        ✓
                      </div>
                    )}
                    {usernameValidation.isValid === false && formData.username.trim() && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400">
                        ✗
                      </div>
                    )}
                  </div>
                  {usernameValidation.isValid === true && (
                    <p className="text-green-400 text-sm mt-1">
                      ✓ Username is available
                    </p>
                  )}
                  {usernameValidation.isValid === false && formData.username.trim() && (
                    <p className="text-red-400 text-sm mt-1">
                      ✗ {usernameValidation.errorMessage || 'Username already exists'}
                    </p>
                  )}
                  {errors.username && (
                    <p className="text-red-400 text-sm mt-1">{errors.username}</p>
                  )}
                </div>
              </div>

              {/* Phone and Country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-600'
                    }`}
                    placeholder="+27 74 449 3251"
                    required
                  />
                  {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Country *
                  </label>
                  <select
                    name="countryOfResidence"
                    value={formData.countryOfResidence}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors.countryOfResidence ? 'border-red-500' : 'border-gray-600'
                    }`}
                    required
                  >
                    <option value="">Select a country...</option>
                    {allCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.countryOfResidence && <p className="text-red-400 text-sm mt-1">{errors.countryOfResidence}</p>}
                </div>
              </div>

              {/* Sponsor Username */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Sponsor Username (Optional)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="sponsorUsername"
                    value={formData.sponsorUsername}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors.sponsorUsername || sponsorValidation.isValid === false ? 'border-red-500' :
                      sponsorValidation.isValid === true ? 'border-green-500' : 'border-gray-600'
                    }`}
                    placeholder="sponsor_username"
                  />
                  {sponsorValidation.isValidating && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                    </div>
                  )}
                  {sponsorValidation.isValid === true && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-400">
                      ✓
                    </div>
                  )}
                  {sponsorValidation.isValid === false && formData.sponsorUsername.trim() && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400">
                      ✗
                    </div>
                  )}
                </div>
                {sponsorValidation.isValid === true && sponsorValidation.sponsorName && (
                  <p className="text-green-400 text-sm mt-1">
                    ✓ Sponsor found: {sponsorValidation.sponsorName}
                  </p>
                )}
                {sponsorValidation.isValid === false && formData.sponsorUsername.trim() && (
                  <p className="text-red-400 text-sm mt-1">
                    ✗ Sponsor not found
                  </p>
                )}
                {errors.sponsorUsername && (
                  <p className="text-red-400 text-sm mt-1">{errors.sponsorUsername}</p>
                )}
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isAdmin"
                    checked={formData.isAdmin}
                    onChange={handleInputChange}
                    className="mr-2 rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-300">Grant admin privileges</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onChange={handleInputChange}
                    className="mr-2 rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-300">Send welcome email with login credentials</span>
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    emailValidation.isValidating ||
                    usernameValidation.isValidating ||
                    sponsorValidation.isValidating ||
                    emailValidation.isValid === false ||
                    usernameValidation.isValid === false ||
                    !!(formData.sponsorUsername.trim() && sponsorValidation.isValid === false)
                  }
                  className="px-6 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  )}
                  {loading ? 'Creating User...' :
                   emailValidation.isValidating || usernameValidation.isValidating || sponsorValidation.isValidating ? 'Validating...' :
                   'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showEmailVerification && createdUserId && (
        <EmailVerificationModal
          isOpen={showEmailVerification}
          onClose={handleEmailVerificationSkip}
          userId={createdUserId}
          email={formData.email}
          purpose="registration"
          onVerificationSuccess={handleEmailVerificationSuccess}
          title="Verify New User Email"
          description="A verification code has been sent to the user's email address."
        />
      )}
    </>
  )
}
